// ReferralEmailsQuestion.tsx
import * as React from "react";
import {
  Question,
  Serializer,
  QuestionFactory,
  SurveyError
} from "survey-core";
import { ReactQuestionFactory } from "survey-react-ui";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { loc } from "../i18n/survey";

const ClaimedCard = styled("div")`
    display: flex;
    margin-bottom: 20px;
`

/** Question model: the "value" of this question is a string[] of emails */
export class QuestionShareGuestPasses extends Question {
  getType(): string {
    return "guestPassInvites";
  }

  protected onCheckForErrors(errors: SurveyError[]): void {
    const arr: string[] = this.emails;

    if (!arr.length) {
      errors.push(new SurveyError(i18n.t("quiz.guestPass.errors.atLeastOne")));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const email of arr) {
      if (!emailRegex.test(email)) {
        errors.push(new SurveyError(i18n.t("quiz.guestPass.errors.invalid", { email })));
        return;
      }
    }

    const seen = new Set<string>();
    for (const email of arr) {
      const lower = email.toLowerCase();
      if (seen.has(lower)) {
        errors.push(new SurveyError(i18n.t("quiz.guestPass.errors.duplicate", { email })));
        return;
      }
      seen.add(lower);
    }

  }

  // Public JSON-configurable props
  get maxInvites(): number {
    return this.getPropertyValue("maxInvites", 3);
  }

  set maxInvites(v: number) {
    this.setPropertyValue("maxInvites", v);
  }

  get placeholder(): string {
    return this.getPropertyValue("placeholder", "Friend’s email");
  }

  set placeholder(v: string) {
    this.setPropertyValue("placeholder", v);
  }

  /** Accessor for emails stored in SurveyJS `value` */
  get emails(): string[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  set emails(val: string[]) {
    this.value = Array.isArray(val) ? val : [];
  }
}

// Register the model with the SurveyJS serializer and factory
Serializer.addClass(
  "guestPassInvites",
  [
    {name: "maxInvites:number", default: 3},
    {name: "placeholder", default: "Friend’s email"}
  ],
  () => new QuestionShareGuestPasses(""),
  "question"
);
QuestionFactory.Instance.registerQuestion("guestPassInvites", (name) => {
  return new QuestionShareGuestPasses(name);
});

/** React renderer */
const ReferralEmailsView: React.FC<{ question: QuestionShareGuestPasses }> = ({question}) => {
  const { t } = useTranslation();
  const [rows, setRows] = React.useState<string[]>(() =>
    question.emails.length ? question.emails : [""]
  );

  const updateSurveyValue = (newRows: string[]) => {
    setRows(newRows);
    question.emails = newRows.map((r) => r.trim());
  };

  const addRow = () => {
    if (rows.length < question.maxInvites) {
      updateSurveyValue([...rows, ""]);
    }
  };

  const removeRow = (idx: number) => {
    const newRows = rows.filter((_, i) => i !== idx);
    updateSurveyValue(newRows.length ? newRows : [""]);
  };

  return (
    <div className="new-quote-container centered">
      <div style={{margin: "auto", width: "100%", wordWrap: "break-word"}} className="new-quote-container centered">
        <div className="splash-header">
          <h1 className="no-word-break">{t("quiz.guestPass.title")}</h1>
          <div style={{whiteSpace: 'pre-wrap', overflowWrap: 'break-word', marginBottom: "12px"}} className="quote">
            {t("quiz.guestPass.subtitle")}
          </div>
        </div>
      </div>

      <ClaimedCard>
        <img src="/guest-pass-card.png" alt={t("quiz.guestPass.cardAlt")}/>
      </ClaimedCard>

      {/* Inputs */}
      <div style={{width: "100%"}}>
        {rows.map((val, i) => (
          <div
            key={i}
            style={{width: "100%", display: "flex", gap: 8, alignItems: "center", marginBottom: 8}}
          >
            <input
              type="email"
              style={{flex: 1, border: "1px solid grey", borderRadius: "10px", padding: "16px 20px", fontSize: "15px"}}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault(); // stop SurveyJS from advancing
                }
              }}
              //placeholder={question.placeholder}
              placeholder={t("quiz.guestPass.placeholder")}
              value={val}
              disabled={question.isReadOnly}
              onChange={(e) => {
                const newRows = rows.map((r, j) => (i === j ? e.target.value : r));
                updateSurveyValue(newRows);
              }}
            />
            {rows.length > 1 && (
              <button
                style={{
                  backgroundColor: "transparent",
                  border: 0,
                  fontSize: 24,
                  fontWeight: "bolder",
                  cursor: "pointer"
                }}
                type="button"
                onClick={() => removeRow(i)}
                disabled={question.isReadOnly}
                aria-label={t("quiz.guestPass.remove")}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add another */}
      {rows.length < question.maxInvites && (
        <button
          type="button"
          onClick={addRow}
          disabled={question.isReadOnly}
          style={{
            fontSize: "14px",
            color: "#0b6dff",
            marginTop: 4,
            backgroundColor: "transparent",
            border: 0,
            width: "100%",
            textAlign: "start",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          {t("quiz.guestPass.addAnother")}
        </button>
      )}
    </div>
  );
};

// Register the React renderer with SurveyJS
ReactQuestionFactory.Instance.registerQuestion("guestPassInvites", (props) => {
  return <ReferralEmailsView question={(props as any).question as QuestionShareGuestPasses}/>;
});

export default ReferralEmailsView;

export const guestPassQuestionComponent = {
  type: "guestPassInvites",
  name: "guestPassInvites",
  title: loc("quiz.guestPass.questionTitle"),
  titleLocation: "hidden",
  placeholder: "Email",
  maxInvites: 3,
  cssClasses: {
    root: "guest-pass-question"
  }
};